import type React from "react";
import { useEffect, useState } from "react";
import { Responsive, WidthProvider, type Layout, type LayoutItem } from "react-grid-layout/legacy";
import "react-grid-layout/css/styles.css";

import EditableTile from "./EditableTile";
import { reconcile } from "./reconcile";
import type { DashboardView, SectionDef, TileDef, TileRenderCtx } from "./types";

// `WidthProvider(Responsive)` must be constructed at module scope: re-wrapping on
// every render remounts the grid (loses width, flickers, re-fires onLayoutChange).
const ResponsiveGrid = WidthProvider(Responsive);

const COLS = { lg: 12, md: 12, sm: 6, xs: 1, xxs: 1 } as const;
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 } as const;
// Row unit chosen so the registry's tile heights (booking-pace-tiles.tsx) map to
// real card pixels: a stat card (h:2) ≈ 190px, a chart card (h:3) ≈ 290px. rgl
// adds its default 10px margin between rows, so a tile of height `h` occupies
// `ROW_HEIGHT*h + 10*(h-1)` px. Tuned against /tmp screenshots so slots fully
// contain their cards with no pixel overflow.
const ROW_HEIGHT = 90;

export interface DashboardGridProps {
    sections: SectionDef[];
    registry: TileDef[];
    view: DashboardView;
    editMode: boolean;
    onLayoutChange: (sectionId: string, items: LayoutItem[]) => void;
    onToggleHide: (id: string) => void;
    onMove: (id: string, dir: "prev" | "next") => void;
    renderCtx: TileRenderCtx;
}

/**
 * Respects `prefers-reduced-motion`: when set, we drop a class on the wrapper that
 * suppresses rgl's drag/placeholder CSS transitions (see the scoped <style> below).
 */
function useReducedMotion(): boolean {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === "undefined" || !window.matchMedia) return false;
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = () => setReduced(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return reduced;
}

/**
 * True at/above the desktop (lg) breakpoint. Below it, the editor bypasses
 * react-grid-layout (whose fixed 120px rowHeight would clip/overlap cards in a
 * 1-col layout) and renders a natural-height read-only stack instead. Defaults
 * to desktop when matchMedia is unavailable (SSR/jsdom safety).
 */
function useIsDesktop(): boolean {
    const [desktop, setDesktop] = useState(() => {
        if (typeof window === "undefined" || !window.matchMedia) return true;
        return window.matchMedia("(min-width: 1024px)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(min-width: 1024px)");
        const onChange = () => setDesktop(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return desktop;
}

function DashboardGrid({
    sections,
    registry,
    view,
    editMode,
    onLayoutChange,
    onToggleHide,
    onMove,
    renderCtx,
}: DashboardGridProps): React.ReactElement {
    const reducedMotion = useReducedMotion();
    const isDesktop = useIsDesktop();
    const { layouts, hidden } = reconcile(view, sections, registry, COLS.lg);

    const tilesById = new Map(registry.map((t) => [t.id, t]));

    return (
        <div className={reducedMotion ? "dashboard-grid-no-motion" : undefined}>
            {reducedMotion && (
                <style>{`
                    .dashboard-grid-no-motion .react-grid-item,
                    .dashboard-grid-no-motion .react-grid-item.cssTransforms {
                        transition: none !important;
                    }
                `}</style>
            )}
            {sections.map((section) => {
                const sectionItems = layouts[section.id] ?? [];

                // Read mode omits hidden tiles entirely; edit mode keeps them (dimmed)
                // so they can be un-hidden. The grid item set drives both the rgl
                // layout array and the children, so derive them together.
                const visibleItems = editMode
                    ? sectionItems
                    : sectionItems.filter((it) => !hidden.has(it.i));

                const lastIndex = visibleItems.length - 1;

                // Below the desktop breakpoint, bypass rgl entirely: its fixed
                // ROW_HEIGHT slots clip/overlap cards at 1-col widths. Render a
                // natural-height, read-only vertical stack in saved reading order
                // (edit mode is gated to desktop at the page level, so mobile is
                // always read-only — no drag/resize/move chrome needed here).
                if (!isDesktop) {
                    const stackItems = [...visibleItems].sort((a, b) => a.y - b.y || a.x - b.x);
                    return (
                        <div key={section.id} data-testid={`section-grid-${section.id}`}>
                            <div
                                data-testid={`section-stack-${section.id}`}
                                className="grid grid-cols-1 gap-4"
                            >
                                {stackItems.map((gridItem) => {
                                    const tile = tilesById.get(gridItem.i);
                                    if (!tile) return null;
                                    return (
                                        <div key={tile.id} data-testid={`tile-${tile.id}`} data-tile-type={tile.type}>
                                            {tile.render(renderCtx)}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={section.id} data-testid={`section-grid-${section.id}`}>
                        <ResponsiveGrid
                            className="layout"
                            breakpoints={BREAKPOINTS}
                            cols={COLS}
                            rowHeight={ROW_HEIGHT}
                            isBounded
                            draggableHandle=".tile-drag-handle"
                            // East-only handle (width-only resize). rgl emits the
                            // handle span whenever `resizeHandles` is set, even with
                            // `isResizable={false}`, so omit it entirely in read mode.
                            resizeHandles={editMode ? ["e"] : []}
                            isDraggable={editMode}
                            isResizable={editMode}
                            layouts={{
                                lg: visibleItems,
                                md: visibleItems,
                                sm: visibleItems,
                                xs: visibleItems,
                                xxs: visibleItems,
                            }}
                            // rgl hands back a readonly `Layout`; copy to the mutable
                            // `LayoutItem[]` the contract (and useDashboardLayout) expects.
                            onLayoutChange={(items: Layout) => onLayoutChange(section.id, [...items])}
                        >
                            {visibleItems.map((gridItem, index) => {
                                const tile = tilesById.get(gridItem.i);
                                if (!tile) return null;
                                return (
                                    <div key={tile.id} data-grid={gridItem} className="h-full">
                                        <EditableTile
                                            tile={tile}
                                            title={tile.title}
                                            editMode={editMode}
                                            hidden={hidden.has(tile.id)}
                                            isFirst={index === 0}
                                            isLast={index === lastIndex}
                                            onToggleHide={onToggleHide}
                                            onMovePrev={(id) => onMove(id, "prev")}
                                            onMoveNext={(id) => onMove(id, "next")}
                                        >
                                            {tile.render(renderCtx)}
                                        </EditableTile>
                                    </div>
                                );
                            })}
                        </ResponsiveGrid>
                    </div>
                );
            })}
        </div>
    );
}

export default DashboardGrid;
